import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef, useState } from "react";
import { ExternalLink } from "lucide-react";
import { Button } from "./ui/button";

const categories = ["Todos", "Redes Sociales", "Diseño Web", "Branding", "Marketing"];

const projects = [
  {
    id: 1,
    title: "Marca de Moda Urban",
    category: "Redes Sociales",
    image: "https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d?crop=entropy&cs=srgb&fm=jpg&w=600&q=80",
    description: "Estrategia de contenido visual para marca de streetwear",
  },
  {
    id: 2,
    title: "Restaurante Gourmet",
    category: "Diseño Web",
    image: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?crop=entropy&cs=srgb&fm=jpg&w=600&q=80",
    description: "Sitio web elegante con sistema de reservaciones",
  },
  {
    id: 3,
    title: "Startup Tecnológica",
    category: "Branding",
    image: "https://images.unsplash.com/photo-1559136555-9303baea8ebd?crop=entropy&cs=srgb&fm=jpg&w=600&q=80",
    description: "Identidad visual completa y materiales corporativos",
  },
  {
    id: 4,
    title: "E-commerce de Belleza",
    category: "Marketing",
    image: "https://images.unsplash.com/photo-1596462502278-27bfdc403348?crop=entropy&cs=srgb&fm=jpg&w=600&q=80",
    description: "Campaña de lanzamiento con ROI del 320%",
  },
  {
    id: 5,
    title: "Consultora Financiera",
    category: "Diseño Web",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?crop=entropy&cs=srgb&fm=jpg&w=600&q=80",
    description: "Portal corporativo con dashboard de clientes",
  },
  {
    id: 6,
    title: "Gym & Fitness",
    category: "Redes Sociales",
    image: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?crop=entropy&cs=srgb&fm=jpg&w=600&q=80",
    description: "Contenido motivacional con +50K seguidores",
  },
];

export default function PortfolioSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const [activeCategory, setActiveCategory] = useState("Todos");

  const filteredProjects = activeCategory === "Todos" 
    ? projects 
    : projects.filter(p => p.category === activeCategory);

  return (
    <section
      id="portfolio"
      ref={ref}
      className="py-24 lg:py-32 bg-[#0a0a0a] relative"
      data-testid="portfolio-section"
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <p className="overline mb-4">Portafolio</p>
          <h2 className="font-playfair text-3xl lg:text-5xl font-bold text-white mb-6">
            Proyectos que{" "}
            <span className="italic text-[#D4AF37]">inspiran</span>
          </h2>
          <p className="text-[#A3A3A3] max-w-2xl mx-auto text-sm lg:text-base">
            Una selección de nuestros trabajos más destacados en diferentes áreas
            de producción creativa y marketing digital.
          </p>
        </motion.div>

        {/* Category Filter - Horizontal scroll on mobile */}
        <div className="flex gap-2 lg:gap-3 mb-10 lg:mb-12 overflow-x-auto pb-2 -mx-6 px-6 lg:mx-0 lg:px-0 lg:justify-center lg:flex-wrap scrollbar-hide">
          {categories.map((category) => (
            <Button
              key={category}
              onClick={() => setActiveCategory(category)}
              variant="ghost"
              className={`px-4 lg:px-5 py-2 rounded-full text-sm font-manrope whitespace-nowrap transition-colors duration-200 flex-shrink-0 ${
                activeCategory === category
                  ? "bg-[#D4AF37] text-[#050505] hover:bg-[#D4AF37]"
                  : "text-[#A3A3A3] hover:text-white hover:bg-white/5"
              }`}
              data-testid={`portfolio-filter-${category.toLowerCase().replace(' ', '-')}`}
            >
              {category}
            </Button>
          ))}
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
          {filteredProjects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ delay: index * 0.05, duration: 0.4 }}
              className="group relative aspect-[4/3] overflow-hidden rounded-lg cursor-pointer"
              data-testid={`portfolio-item-${project.id}`}
            >
              {/* Image */}
              <img
                src={project.image}
                alt={project.title}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                loading="lazy"
              />

              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              {/* Content */}
              <div className="absolute inset-0 p-4 lg:p-6 flex flex-col justify-end opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <span className="text-[#D4AF37] text-xs uppercase tracking-wider mb-1 lg:mb-2">
                  {project.category}
                </span>
                <h3 className="font-playfair text-base lg:text-xl font-bold text-white mb-1 lg:mb-2">
                  {project.title}
                </h3>
                <p className="text-[#A3A3A3] text-xs lg:text-sm hidden lg:block">
                  {project.description}
                </p>
              </div>

              {/* Border */}
              <div className="absolute inset-0 border border-white/0 group-hover:border-[#D4AF37]/50 rounded-lg transition-colors duration-300 pointer-events-none" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
